import * as XLSX from 'xlsx';
import { apiFetch } from '../shared/apiClient';
import { WorkLogEntry } from '../shared/types';

export interface EstimationAccuracyPeriod {
  period: string;
  storyCount: number;
  totalEstimateHours: number;
  totalLoggedHours: number;
  deviationHours?: number;
  deviationPct?: number | null;
  within10PercentCount: number;
  within10PercentPct: number;
  avgDeviationPct: number | null;
}

export interface EstimationAccuracyResponse {
  byRelease: EstimationAccuracyPeriod[];
  byQuarter: EstimationAccuracyPeriod[];
  overall: Omit<EstimationAccuracyPeriod, 'period'>;
  kpi: {
    lastReleasesCount: number;
    lastReleasesWithin10Percent: number | null;
    totalStoriesWithEstimate: number;
  };
}

export interface EstimationAccuracyDetailSummary {
  storyCount: number;
  totalEstimateHours: number;
  totalLoggedHours: number;
  deviationHours: number;
  deviationPct: number | null;
  within10PercentCount: number;
  within10PercentPct: number;
  avgDeviationPct: number | null;
}

export interface EstimationAccuracyStory {
  key: string;
  summary: string;
  fixVersion: string;
  estimate: number;
  logged: number;
  deviationHours: number;
  deviationPct: number | null;
  status: string | null;
}

export interface EstimationAccuracyDetailResponse {
  periodLabel: string;
  summary: EstimationAccuracyDetailSummary;
  stories: EstimationAccuracyStory[];
}

/** Summary + optional first-release detail from `getEstimationAccuracy({ includeInitialDetail: true })`. */
export type EstimationAccuracyResponseWithOptionalDetail = EstimationAccuracyResponse & {
  initialDetail?: EstimationAccuracyDetailResponse;
};

export type EstimationDetailPeriodType = 'release' | 'quarter' | 'daterange';

export type AnalyticsDateScope = {
  startDate?: string;
  endDate?: string;
  fixVersion?: string;
};

export type AnalyticsDoneCountResponse = {
  totalDoneIssues: number;
  totalHoursDone: number;
  byType: Array<{ type: string; count: number }>;
};

function analyticsScopeQuery(params?: AnalyticsDateScope): string {
  const qs = new URLSearchParams();
  if (params?.startDate) qs.set('startDate', params.startDate);
  if (params?.endDate) qs.set('endDate', params.endDate);
  if (params?.fixVersion) qs.set('fixVersion', params.fixVersion);
  const query = qs.toString();
  return query ? `?${query}` : '';
}

export class ApiService {
  static async saveWorkLogEntries(entries: WorkLogEntry[]): Promise<void> {
    const response = await apiFetch('/api/worklog/save', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(entries),
    });

    if (!response.ok) {
      throw new Error(`Failed to save worklog entries: ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Worklog entries saved:', result);
  }

  static async getWorkLogEntries(): Promise<WorkLogEntry[]> {
    const response = await apiFetch('/api/worklog');

    if (!response.ok) {
      throw new Error(`Failed to fetch worklog entries: ${response.statusText}`);
    }

    return await response.json();
  }

  static async getWorkLogEntriesByDateRange(
    startDate: string,
    endDate: string
  ): Promise<WorkLogEntry[]> {
    const response = await apiFetch(
      `/api/worklog/date-range?startDate=${startDate}&endDate=${endDate}`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch worklog entries by date range: ${response.statusText}`);
    }

    return await response.json();
  }

  /** Distinct Done issues. Optional Period dates: unique Done with SUM(time_spent)>0 in range. */
  static async getTotalDoneIssues(
    params?: AnalyticsDateScope
  ): Promise<AnalyticsDoneCountResponse> {
    const response = await apiFetch(`/api/analytics/done-count${analyticsScopeQuery(params)}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch done count: ${response.statusText}`);
    }
    const raw = (await response.json()) as {
      totalDoneIssues?: number;
      count?: number;
      totalHoursDone?: number;
      byType?: unknown;
    };
    const totalDoneIssues =
      typeof raw.totalDoneIssues === 'number'
        ? raw.totalDoneIssues
        : typeof raw.count === 'number'
          ? raw.count
          : 0;
    const byType = Array.isArray(raw.byType) ? raw.byType : [];
    return {
      totalDoneIssues,
      totalHoursDone: typeof raw.totalHoursDone === 'number' ? raw.totalHoursDone : 0,
      byType: byType.map((row) => {
        const o = row as Record<string, unknown>;
        return {
          type: typeof o.type === 'string' ? o.type : 'Unknown',
          count: typeof o.count === 'number' ? o.count : 0,
        };
      }),
    };
  }

  /** Server-side aggregates for analytics cards (no full worklog download). */
  static async getAnalyticsSummaryStats(params?: AnalyticsDateScope): Promise<{
    totalHours: number;
    uniqueAuthors: number;
    avgHoursPerDoneIssueWithWorklog: string;
  }> {
    const response = await apiFetch(`/api/analytics/summary-stats${analyticsScopeQuery(params)}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch summary stats: ${response.statusText}`);
    }
    const raw = (await response.json()) as {
      totalHours?: number;
      uniqueAuthors?: number;
      avgHoursPerDoneIssueWithWorklog?: string;
    };
    return {
      totalHours: typeof raw.totalHours === 'number' ? raw.totalHours : 0,
      uniqueAuthors: typeof raw.uniqueAuthors === 'number' ? raw.uniqueAuthors : 0,
      avgHoursPerDoneIssueWithWorklog:
        typeof raw.avgHoursPerDoneIssueWithWorklog === 'string'
          ? raw.avgHoursPerDoneIssueWithWorklog
          : '0',
    };
  }

  /** Server-side aggregates for Overview charts (avoids loading the full worklog into the browser). */
  static async getAnalyticsChartSeries(params?: {
    startDate?: string;
    endDate?: string;
    fixVersion?: string;
  }): Promise<{
    daily: Array<{
      date: string;
      timeSpent: number;
      developers: Array<{ name: string; hours: number }>;
    }>;
    byType: Array<{ type: string; rowCount: number; hours: number; issueCount: number }>;
    byAuthorType: Array<{ author: string; type: string; hours: number }>;
  }> {
    const qs = new URLSearchParams();
    if (params?.startDate) qs.set('startDate', params.startDate);
    if (params?.endDate) qs.set('endDate', params.endDate);
    if (params?.fixVersion) qs.set('fixVersion', params.fixVersion);
    const query = qs.toString();
    const url = `/api/analytics/chart-series${query ? `?${query}` : ''}`;
    const response = await apiFetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch chart series: ${response.statusText}`);
    }
    const raw = (await response.json()) as {
      daily?: unknown;
      byType?: unknown;
      byAuthorType?: unknown;
    };
    const daily = Array.isArray(raw.daily) ? raw.daily : [];
    const byType = Array.isArray(raw.byType) ? raw.byType : [];
    const byAuthorType = Array.isArray(raw.byAuthorType) ? raw.byAuthorType : [];
    return {
      daily: daily.map((d) => {
        const o = d as Record<string, unknown>;
        return {
          date: typeof o.date === 'string' ? o.date : '',
          timeSpent: typeof o.timeSpent === 'number' ? o.timeSpent : 0,
          developers: Array.isArray(o.developers)
            ? o.developers.map((x) => {
                const p = x as Record<string, unknown>;
                return {
                  name: typeof p.name === 'string' ? p.name : '',
                  hours: typeof p.hours === 'number' ? p.hours : 0,
                };
              })
            : [],
        };
      }),
      byType: byType.map((t) => {
        const o = t as Record<string, unknown>;
        const rowCount = typeof o.rowCount === 'number' ? o.rowCount : 0;
        return {
          type: typeof o.type === 'string' ? o.type : 'Unknown',
          rowCount,
          hours: typeof o.hours === 'number' ? o.hours : 0,
          issueCount: typeof o.issueCount === 'number' ? o.issueCount : rowCount,
        };
      }),
      byAuthorType: byAuthorType.map((r) => {
        const o = r as Record<string, unknown>;
        return {
          author: typeof o.author === 'string' ? o.author : '',
          type: typeof o.type === 'string' ? o.type : 'Unknown',
          hours: typeof o.hours === 'number' ? o.hours : 0,
        };
      }),
    };
  }

  /**
   * Period options for the unified PeriodPicker on /analytics.
   * Returns sprints (with start/end dates), releases (fix versions) and
   * quarters (computed from issue created dates). One call feeds every mode
   * of the picker so it does not fan out into multiple requests.
   */
  static async getAnalyticsPeriodOptions(): Promise<{
    sprints: Array<{
      id: string;
      name: string;
      startDate?: string;
      endDate?: string;
      state?: 'active' | 'closed' | 'future';
      issueCount?: number;
    }>;
    releases: Array<{
      fixVersion: string;
      releaseDate?: string;
      minCreated?: string;
      maxUpdated?: string;
      planStartDate?: string;
      planEndDate?: string;
      storyCount?: number;
      issueCount?: number;
    }>;
    quarters: Array<{
      id: string;
      label: string;
      startDate: string;
      endDate: string;
      issueCount?: number;
    }>;
  }> {
    const response = await apiFetch('/api/analytics/period-options');
    if (!response.ok) {
      throw new Error(`Failed to fetch period options: ${response.statusText}`);
    }
    const raw = (await response.json()) as {
      sprints?: unknown;
      releases?: unknown;
      quarters?: unknown;
    };
    const sprints = Array.isArray(raw.sprints) ? raw.sprints : [];
    const releases = Array.isArray(raw.releases) ? raw.releases : [];
    const quarters = Array.isArray(raw.quarters) ? raw.quarters : [];
    return {
      sprints: sprints.map((s) => {
        const o = s as Record<string, unknown>;
        return {
          id: typeof o.id === 'string' ? o.id : '',
          name: typeof o.name === 'string' ? o.name : '',
          startDate: typeof o.startDate === 'string' ? o.startDate : undefined,
          endDate: typeof o.endDate === 'string' ? o.endDate : undefined,
          state:
            o.state === 'active' || o.state === 'closed' || o.state === 'future'
              ? o.state
              : undefined,
          issueCount: typeof o.issueCount === 'number' ? o.issueCount : undefined,
        };
      }),
      releases: releases.map((r) => {
        const o = r as Record<string, unknown>;
        return {
          fixVersion: typeof o.fixVersion === 'string' ? o.fixVersion : '',
          releaseDate: typeof o.releaseDate === 'string' ? o.releaseDate : undefined,
          minCreated: typeof o.minCreated === 'string' ? o.minCreated : undefined,
          maxUpdated: typeof o.maxUpdated === 'string' ? o.maxUpdated : undefined,
          planStartDate: typeof o.planStartDate === 'string' ? o.planStartDate : undefined,
          planEndDate: typeof o.planEndDate === 'string' ? o.planEndDate : undefined,
          storyCount: typeof o.storyCount === 'number' ? o.storyCount : undefined,
          issueCount: typeof o.issueCount === 'number' ? o.issueCount : undefined,
        };
      }),
      quarters: quarters.map((q) => {
        const o = q as Record<string, unknown>;
        return {
          id: typeof o.id === 'string' ? o.id : '',
          label: typeof o.label === 'string' ? o.label : '',
          startDate: typeof o.startDate === 'string' ? o.startDate : '',
          endDate: typeof o.endDate === 'string' ? o.endDate : '',
          issueCount: typeof o.issueCount === 'number' ? o.issueCount : undefined,
        };
      }),
    };
  }

  /**
   * Distinct worklog authors for the current product (server-aggregated).
   * Used by CalendarPage author mapping so former employees who no longer
   * appear in Jira's assignable-users list are still mappable.
   */
  static async getWorklogAuthors(): Promise<string[]> {
    const response = await apiFetch('/api/analytics/worklog-authors');
    if (!response.ok) {
      throw new Error(`Failed to fetch worklog authors: ${response.statusText}`);
    }
    const data = (await response.json()) as { authors?: unknown };
    if (!Array.isArray(data.authors)) return [];
    return data.authors
      .filter((a): a is string => typeof a === 'string' && a.trim().length > 0)
      .map((a) => a.trim());
  }

  static async clearAllWorkLogEntries(): Promise<void> {
    const response = await apiFetch('/api/worklog', {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`Failed to clear worklog entries: ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Worklog entries cleared:', result);
  }

  static async getEstimationAccuracy(options?: {
    includeInitialDetail?: boolean;
  }): Promise<EstimationAccuracyResponseWithOptionalDetail> {
    const qs = options?.includeInitialDetail === true ? '?includeInitialDetail=1' : '';
    const response = await apiFetch(`/api/estimation-accuracy${qs}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch estimation accuracy: ${response.statusText}`);
    }
    return response.json();
  }

  static async getEstimationAccuracyDetail(
    type: EstimationDetailPeriodType,
    options: { period?: string; startDate?: string; endDate?: string }
  ): Promise<EstimationAccuracyDetailResponse> {
    const params = new URLSearchParams({ type });
    if (type === 'release' || type === 'quarter') {
      if (options.period) params.set('period', options.period);
    } else {
      if (options.startDate) params.set('startDate', options.startDate);
      if (options.endDate) params.set('endDate', options.endDate);
    }
    const response = await apiFetch(`/api/estimation-accuracy/detail?${params.toString()}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch estimation accuracy detail: ${response.statusText}`);
    }
    return response.json();
  }

  static exportToCSV(data: WorkLogEntry[]): void {
    const headers = [
      'Author',
      'Key',
      'Start Date',
      'Time Spent (hrs)',
      'Status',
      'Type',
      'Summary',
    ];
    const csvContent = [
      headers.join(','),
      ...data.map((entry) =>
        [
          `"${entry.author}"`,
          `"${entry.key}"`,
          `"${entry.startDate}"`,
          entry.timeSpent,
          `"${entry.status}"`,
          `"${entry.type}"`,
          `"${entry.summary.replace(/"/g, '""')}"`, // Экранируем кавычки в summary
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `worklog_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  static exportToExcel(data: WorkLogEntry[]): void {
    // Создаем настоящий XLSX файл
    const headers = [
      'Author',
      'Key',
      'Start Date',
      'Time Spent (hrs)',
      'Status',
      'Type',
      'Summary',
    ];

    // Подготавливаем данные для Excel
    const excelData = data.map((entry) => [
      entry.author,
      entry.key,
      entry.startDate,
      Number(entry.timeSpent.toFixed(1)), // Округляем до одной десятой
      entry.status,
      entry.type,
      entry.summary,
    ]);

    // Добавляем строку с итогами
    const totalTime = data.reduce((sum, entry) => sum + entry.timeSpent, 0);
    excelData.push(['Total', '', '', totalTime.toFixed(1), '', '', '']);

    // Создаем рабочую книгу
    const wb = XLSX.utils.book_new();

    // Создаем рабочий лист
    const ws = XLSX.utils.aoa_to_sheet([headers, ...excelData]);

    // Настраиваем ширину колонок
    const colWidths = [
      { wch: 20 }, // Author
      { wch: 15 }, // Key
      { wch: 25 }, // Start Date
      { wch: 15 }, // Time Spent
      { wch: 15 }, // Status
      { wch: 10 }, // Type
      { wch: 50 }, // Summary
    ];
    ws['!cols'] = colWidths;

    // Добавляем стили для заголовков
    const headerRange = XLSX.utils.decode_range(ws['!ref'] || 'A1:G1');
    for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!ws[cellAddress]) continue;
      ws[cellAddress].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: 'F8FAFC' } },
        alignment: { horizontal: 'center' },
      };
    }

    // Добавляем стили для строки итогов
    const lastRow = excelData.length;
    for (let col = 0; col < headers.length; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: lastRow, c: col });
      if (!ws[cellAddress]) continue;
      ws[cellAddress].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: 'F1F5F9' } },
      };
    }

    // Добавляем лист в книгу
    XLSX.utils.book_append_sheet(wb, ws, 'Work Log Data');

    // Генерируем файл
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

    // Создаем blob и скачиваем
    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `worklog_export_${new Date().toISOString().split('T')[0]}.xlsx`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
